import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skill } from "@/models";
import { isDateToday, toDateString } from "@/utilities";
import { useWeeklyPlan } from "@/hooks";
import { AmiraResources } from "@/models/AmiraResources.interface";

// Store
import { useSelector } from "react-redux";
import { selectResourcesLoading, selectWeeklyPlanIsLoading } from "@/store/slices";

// Components
import SkillCard from "./SkillCard";
import { ResourceListPopout } from "./ResourceListPopout";
import { ChangeInMastery } from "../PlannerTypes";

// Resources and assets
import "./LessonCard.scss";
import rightIcon from "@/images/icons/right-icon.svg";
import downIcon from "@/images/icons/down-icon.svg";
import "./LessonCard.scss";
import { isBeforeCurrentWeek } from "@/utilities/isBeforeCurrentWeek";
import { Carousel } from "react-bootstrap";
import { SkillType } from "@/enums/SkillType";
import { NotchedContainer } from "@/components/Global/NotchedContainer/NotchedContainer";
import { getUserData } from "@/services/API";
import { LessonDatePicker } from "./LessonDatePicker";
import { ArrowIcon } from "@/components/Global";
import { MasteryTimeBooked } from "@/enums/MasteryStatus";

export interface LessonCardProps {
  id: string;
  lessonPlanId: string;
  lessonName: string;
  dateFrom: string;
  name: string;
  description: string;
  resources: AmiraResources[]; // maybe resource urls or ids?
  completionPercentage: number;
  skills: Skill[];
  studentAssignments: any;
  handleExpandLessonPress: () => void;
  handleStartDateChange: (date: string) => void;
  handleAssignmentsPress: () => void;
  masteryData?: ChangeInMastery[];
  handleMasteryPress?: () => void;
  skillsMasteryLoading: boolean;
  isUpdatingAssignments: boolean;
  isLastLesson: boolean;
}

export const LessonCard: React.FC<LessonCardProps> = ({
  lessonPlanId,
  dateFrom,
  name,
  resources,
  completionPercentage,
  skills,
  handleStartDateChange,
  handleAssignmentsPress,
  handleExpandLessonPress,
  isLastLesson,
}) => {
  const { t } = useTranslation();
  const nextFriday = new Date();
  const isToday = isDateToday(dateFrom.split(" ")[0]);
  const { getLessonBookingStatus } = useWeeklyPlan();
  const isWeeklyPlanLoading = useSelector(selectWeeklyPlanIsLoading);
  const areResourcesLoading = useSelector(selectResourcesLoading);
  const [isSkillsCollapsed, setIsSkillsCollapsed] = useState(true);
  const [bookingAlert, setBookingAlert] = useState<string | null>(null);
  const dateValue = dateFrom.split(" ")[0];
  const isDateDisabled = isBeforeCurrentWeek(dateValue);
  nextFriday.setDate(nextFriday.getDate() + ((5 - nextFriday.getDay() + 7) % 7));
  nextFriday.setDate(
    nextFriday.getDate() + ((5 - nextFriday.getDay() + 7) % 7)
  );
  const students = useSelector((state: any) => state.reports.roster.data);
  const userData = getUserData();

  const handleSkillsPressed = () => {
    setIsSkillsCollapsed((collapsed) => !collapsed);
  };

  // TODO probably won't be used eventually but since not final designs maybe good for now?
  const handleKeyPress = (event: React.KeyboardEvent, func: () => void) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      func();
    }
  };

  useEffect(() => {
    if (!isWeeklyPlanLoading) {
      /**
       * This is purely a mock for development purposes and to expose the expected output
       */
      // const lessonBookingStatus = {
      //   [MasteryTimeBooked.OVER_BOOKED]: new Set(["12345"]), // single student
      //   [MasteryTimeBooked.UNDER_BOOKED]: new Set(["12345", "67890"]), // multiple students
      //   [MasteryTimeBooked.NO_DATA]: new Set(["12345", "67890", "112233"]), // multiple students
      // } as Record<MasteryTimeBooked, Set<string>>;

     const lessonBookingStatus = getLessonBookingStatus(lessonPlanId);
      if (!lessonBookingStatus || Object.keys(lessonBookingStatus).length === 0)
        return;
      if (Object.keys(lessonBookingStatus).length > 1) {
        setBookingAlert(
          t("BOOKING_ALERT_MULTIPLE", {
            ns: "planner",
            count: Object.values(lessonBookingStatus).reduce(
              (acc, curr) => acc + curr.size,
              0
            ),
          })
        );
      } else {
        const [bookingClassification, reflexes] =
          Object.entries(lessonBookingStatus)?.[0];
        setBookingAlert(
          t(`BOOKING_ALERT_${bookingClassification}`, {
            count: reflexes.size,
            ns: "planner",
          })
        );
      }
    }
  }, [isWeeklyPlanLoading]);

  const dateInputRef = React.useRef<HTMLInputElement>(null);

  const toggleDateInput = () => {
    if (dateInputRef.current && !isDateDisabled && completionPercentage < 100) {
      dateInputRef.current.showPicker();
    }
  };



  const notchedTitle = (
    <div className="notched-container" onClick={toggleDateInput}>
      <span className="form-label visually-hidden">
        {t("start_date", {ns: "translation"})}
      </span>
      <LessonDatePicker
        date={dateValue}
        onDateChange={handleStartDateChange}
        disabled={completionPercentage === 100}
        align={isLastLesson ? "right" : "left"}
      />
    </div>
  );

  return (
    <NotchedContainer
      title={notchedTitle}
      notchPosition="topRight"
      className={`card-lesson ${isToday ? "card-lesson-today" : ""}`}
    >
      <div >
        <div className="pink-divider-container">
          <div className="pink-divider" />
        </div>

        <header className="card-lesson-meta">
          <h3 className='lesson-name'>{name}</h3>
          <ResourceListPopout
            resources={resources}
            placement="right"
            lesson={name}
            handleOpenDetails={handleExpandLessonPress}
            areResourcesLoading={areResourcesLoading}
          />
        </header>
        <section
          className="card-lesson-completion"
          onClick={handleAssignmentsPress}
          onKeyDown={(e) => handleKeyPress(e, handleAssignmentsPress)}
          tabIndex={0}
          style={{ cursor: "pointer" }}
        >
          <h4 >{t("digital_assignments", {ns: "translation"})}</h4>
          <p className='completion-container'>

            {completionPercentage === 0 ? (
              <span className="not-started">Not Started</span>
            ) : completionPercentage === 100 ? (
              <span className="complete">{t("complete", {ns: "translation"})}</span>
            ) : (
              <span className="in-progress">
                In Progress
                <span className="completion-percent">
                  {completionPercentage}%

                </span>
              </span>
            )}
          </p>
          <div className="progress">
            <div
              className="progress-bar"
              role="progressbar"
              aria-valuenow={completionPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
              style={{
                width: `${completionPercentage}%`,
              }}
            />
          </div>
        </section>


      {skills.length > 0 && (
          <section
            className="card-lesson-skills"
            onKeyDown={(e) => handleKeyPress(e, handleSkillsPressed)}
            tabIndex={0}
          >
            <div
              className="d-flex align-items-center"
              onClick={handleSkillsPressed}
              style={{ cursor: "pointer" }}
            >
              <button
                onClick={handleSkillsPressed}
                className="icon-button pe-none"
              >
                <img
                  src={isSkillsCollapsed ? rightIcon : downIcon}
                  className="icon-button-img"
                  alt="expand and collapse"
                />
              </button>
              <h4>
                {t("focus_skills", {ns: "translation"})} (
                {skills.filter((s) => s.skillType === SkillType.FOCUS).length})
              </h4>
            </div>
            {!isSkillsCollapsed && (
              <div
                className="card-lesson-today-skills"
              >
                {skills.filter((s) => s.skillType === SkillType.FOCUS)
                  .length === 1 ? (
                  // Single skill card without carousel
                  <div className="mb-1">
                    {skills
                      .filter((s) => s.skillType === SkillType.FOCUS)
                      .map((s) => (
                        <SkillCard
                          key={s.skillId}
                          domain={`${s.skillId?.split(".")[0]}`}
                          skillName={`${s.displayName ? s.displayName : s.skillName}`}
                          collapsed={isSkillsCollapsed}
                          skillId={s.skillId}
                          studentId={students.student && students.student.length > 0 ? students.student[0].id : ""}
                          lessonPlanId={s.lessonId || ""}
                          userData={userData}
                        />
                      ))}
                  </div>
                ) : (
                  // Multiple skills with carousel
                  <Carousel
                    slide
                    interval={null}
                    controls={true}
                    indicators={false}
                    className="skills-carousel"
                  >
                    {skills
                      .filter((s) => s.skillType === SkillType.FOCUS)
                      .map((s, i) => (
                        <Carousel.Item
                          key={`${s.skillId}-${i}`}
                          className="mb-1"
                        >
                          <SkillCard
                            domain={`${s.skillId?.split(".")[0]}`}
                            skillName={`${s.displayName ? s.displayName : s.skillName}`}
                            collapsed={isSkillsCollapsed}
                            skillId={s.skillId}
                            studentId={students.student[0].id}
                            lessonPlanId={s.lessonId || ""}
                            userData={userData}
                          />
                        </Carousel.Item>
                      ))}
                  </Carousel>
                )}
              </div>
            )}
          </section>
        )}

{/*
      {skillsMasteryLoading && (
        <div className="p-2">
          <Placeholder heightInRem={14} colWidth={12} />
        </div>
      )}
      {masteryData && masteryData.length > 0 && handleMasteryPress && (
        <section
          onClick={handleMasteryPress}
          onKeyDown={(e) => handleKeyPress(e, handleMasteryPress)}
          tabIndex={0}
          className="card-lesson-mastery p-2"
          style={{ cursor: "pointer" }}>
          <h4>{t("change_in_mastery", {ns: "translation"})}</h4>
          <SkillsMasteryBarChart datasets={masteryData} />
        </section>
      )}
       */}
      <div className='pink-divider-container'>
        <div className="pink-divider" />
      </div>



    <footer className="card-lesson-footer">
        {bookingAlert ? (
          <div className="overbooked-container">
            <span className="text-white">{bookingAlert}</span>
            <span>
              <div
                className="lesson-details-link-small"
                onClick={handleExpandLessonPress}
              >
                <span>View lesson details</span> 
                <i className="icon"><ArrowIcon /></i>
              </div>
            </span>
          </div>
        ) : (
          <div
            className="lesson-details-link"
            onClick={handleExpandLessonPress}
          >
            <span>View lesson details</span>
            <i className="icon"><ArrowIcon /></i>
          </div>
        )}
      </footer>
      </div>

    </NotchedContainer>
  );
};

export default LessonCard;